import React, { useState, useEffect } from 'react';
import { WDBB_SATINWOOD_DEFAULT_TEMPLATES, ARTISAN_EVOLVE_DEFAULT_TEMPLATES, MERCANTILE_BRACE_DEFAULT_TEMPLATES } from '../utils/defaultTemplates';
import Modal from './Modal';
import useModal from '../hooks/useModal';

const TemplateManager = ({ currentData, onLoadTemplate, templateType = 'wdbb-satinwood' }) => {
  const [templates, setTemplates] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const { modalState, hideModal, showSuccess, showError, showWarning } = useModal();

  const storageKey = `evolve-rating-templates-${templateType}`;

  useEffect(() => {
    // Получаем темплейты по умолчанию в зависимости от типа
    const getDefaultTemplates = () => {
      switch (templateType) {
        case 'wdbb-satinwood':
          return WDBB_SATINWOOD_DEFAULT_TEMPLATES;
        case 'artisan-evolve':
          return ARTISAN_EVOLVE_DEFAULT_TEMPLATES;
        case 'mercantile-brace':
          return MERCANTILE_BRACE_DEFAULT_TEMPLATES;
        default:
          return [];
      }
    };

    const loadTemplates = () => {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const userTemplates = JSON.parse(stored);
          // Объединяем темплейты по умолчанию с пользовательскими
          const defaultTemplates = getDefaultTemplates();
          const allTemplates = [...defaultTemplates, ...userTemplates.filter(t => !t.id.startsWith('default-'))];
          setTemplates(allTemplates);
        } else {
          // Если нет сохранённых темплейтов, используем только дефолтные
          setTemplates(getDefaultTemplates());
        }
      } catch (error) {
        console.error('Error loading templates:', error);
        setTemplates(getDefaultTemplates());
      }
    };

    loadTemplates();
  }, [storageKey, templateType]);

  // Функция для получения дефолтных темплейтов (вне useEffect)
  const getDefaultTemplates = () => {
    switch (templateType) {
      case 'wdbb-satinwood':
        return WDBB_SATINWOOD_DEFAULT_TEMPLATES;
      case 'artisan-evolve':
        return ARTISAN_EVOLVE_DEFAULT_TEMPLATES;
      default:
        return [];
    }
  };

  const saveTemplate = () => {
    if (!templateName.trim()) {
      showWarning('Please enter a template name');
      return;
    }

    const newTemplate = {
      id: Date.now().toString(),
      name: templateName,
      createdAt: new Date().toISOString(),
      data: currentData
    };

    // Фильтруем только пользовательские темплейты (не дефолтные)
    const userTemplates = templates.filter(t => !t.id.startsWith('default-'));
    const updatedUserTemplates = [...userTemplates, newTemplate];

    try {
      localStorage.setItem(storageKey, JSON.stringify(updatedUserTemplates));
      // Обновляем список с дефолтными темплейтами
      const defaultTemplates = getDefaultTemplates();
      setTemplates([...defaultTemplates, ...updatedUserTemplates]);
      setTemplateName('');
      setShowSaveDialog(false);
      showSuccess('Template saved successfully!');
    } catch (error) {
      showError('Error saving template: ' + error.message);
    }
  };

  const deleteTemplate = (templateId) => {
    // Не позволяем удалять дефолтные темплейты
    if (templateId.startsWith('default-')) {
      showWarning('Default templates cannot be deleted');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    // Фильтруем только пользовательские темплейты
    const userTemplates = templates.filter(t => !t.id.startsWith('default-') && t.id !== templateId);

    try {
      localStorage.setItem(storageKey, JSON.stringify(userTemplates));
      // Обновляем список с дефолтными темплейтами
      const defaultTemplates = getDefaultTemplates();
      setTemplates([...defaultTemplates, ...userTemplates]);
      setSelectedTemplate('');
      showSuccess('Template deleted successfully!');
    } catch (error) {
      showError('Error deleting template: ' + error.message);
    }
  };

  const loadTemplate = () => {
    const template = templates.find(t => t.id === selectedTemplate);
    if (template) {
      onLoadTemplate(template.data);
      showSuccess('Template loaded successfully!');
    }
  };

  const exportTemplates = () => {
    const dataStr = JSON.stringify(templates, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `evolve-rating-templates-${templateType}-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importTemplates = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        if (Array.isArray(imported)) {
          const merged = [...templates, ...imported.map(t => ({
            ...t,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
          }))];
          localStorage.setItem(storageKey, JSON.stringify(merged));
          setTemplates(merged);
          showSuccess('Templates imported successfully!');
        } else {
          showError('Invalid template file format');
        }
      } catch (error) {
        showError('Error importing templates: ' + error.message);
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return (
    <>
      <Modal
        isOpen={modalState.isOpen}
        onClose={hideModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
      />

      <div className="card">
        <h3>Templates</h3>

        <div className="mb-3">
          <div className="flex">
            <select
              className="form-control"
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              style={{ marginRight: '10px', maxWidth: '490px', minWidth: '290px' }}
            >
              <option value="">Select a template...</option>
              <optgroup label="Default Templates">
                {templates.filter(t => t.id.startsWith('default-')).map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Custom Templates">
                {templates.filter(t => !t.id.startsWith('default-')).map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({new Date(template.createdAt).toLocaleDateString()})
                  </option>
                ))}
              </optgroup>
            </select>

            <button
              type="button"
              className="btn btn-primary"
              onClick={loadTemplate}
              disabled={!selectedTemplate}
              style={{ minHeight: '38px', display: 'inline-flex', alignItems: 'center' }}
            >
              Load
            </button>

            <button
              type="button"
              className="btn btn-danger"
              onClick={() => deleteTemplate(selectedTemplate)}
              disabled={!selectedTemplate}
              style={{ minHeight: '38px', display: 'inline-flex', alignItems: 'center' }}
            >
              Delete
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowSaveDialog(true)}
              style={{ minHeight: '38px', display: 'inline-flex', alignItems: 'center' }}
            >
              Save Current Form
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={exportTemplates}
              disabled={templates.length === 0}
              style={{ minHeight: '38px', display: 'inline-flex', alignItems: 'center' }}
            >
              Export All
            </button>

            <label
              className="btn btn-secondary"
              style={{
                marginBottom: 0,
                minHeight: '38px',
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
            >
              Import
              <input
                type="file"
                accept=".json"
                onChange={importTemplates}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </div>

        {showSaveDialog && (
          <div className="mt-3">
            <div className="form-group">
              <label>Template Name</label>
              <input
                type="text"
                className="form-control"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name"
                onKeyPress={(e) => e.key === 'Enter' && saveTemplate()}
              />
            </div>
            <div className="flex">
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveTemplate}
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowSaveDialog(false);
                  setTemplateName('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TemplateManager;